import { useEffect, useState } from "react";
import { Loader2, ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Stat = ({ label, value, color, testid }) => (
  <div className="neu-raised rounded-2xl p-4" data-testid={testid}>
    <p className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">{label}</p>
    <p className="mt-1 font-display text-3xl font-extrabold" style={{ color }}>{value}</p>
  </div>
);

const QuizCard = ({ q }) => (
  <div className="neu-flat rounded-2xl p-4" data-testid={`quiz-result-${q.layer_id}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="font-display text-lg font-bold">{q.title}</p>
        <p className="text-xs text-[#64748B]">Page {q.page} · {q.attempts} attempts ({q.reader_attempts} by readers)</p>
      </div>
      <div className="flex gap-4 text-right font-mono text-xs">
        <span>avg <b className="text-[#3D3DF0]">{q.avg_score}%</b></span>
        <span>pass <b className="text-[#5FA82E]">{q.pass_rate}%</b></span>
        <span>best <b className="text-[#A62B8A]">{q.best}%</b></span>
      </div>
    </div>
    <div className="mt-4 space-y-1.5">
      {q.question_accuracy.map((a, i) => (
        <div key={i} className="flex items-center gap-2 text-[11px]">
          <span className="w-8 font-mono text-[#64748B]">Q{i + 1}</span>
          <div className="neu-inset h-2.5 flex-1 overflow-hidden rounded-full"><div className="h-full rounded-full sg-gradient" style={{ width: `${a}%` }} /></div>
          <span className="w-12 text-right font-mono">{a}%</span>
        </div>
      ))}
    </div>
    <div className="mt-3 flex flex-wrap gap-1.5">
      {q.recent.map((r, i) => (
        <span key={i} title={new Date(r.created_at).toLocaleString()} className={`rounded-full px-2 py-0.5 font-mono text-[10px] text-white ${r.passed ? "bg-[#5FA82E]" : "bg-[#A62B8A]"}`}>{r.score}%{r.source === "preview" ? " · preview" : ""}</span>
      ))}
    </div>
  </div>
);

const ReaderStats = ({ a }) => {
  const max = Math.max(1, ...a.page_views.map((p) => p.views));
  return (
    <div className="space-y-4" data-testid="reader-analytics">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Book views" value={a.views} color="#0F172A" testid="ra-views" />
        <Stat label="Unique readers" value={a.unique_readers} color="#3D3DF0" testid="ra-readers" />
        <Stat label="Avg pages read" value={a.pages_read_avg} color="#A62B8A" testid="ra-pages" />
        <Stat label="Completion" value={`${a.completion_rate}%`} color="#5FA82E" testid="ra-completion" />
        <Stat label="Hotspot taps" value={a.total_taps} color="#A62B8A" testid="ra-taps" />
        <Stat label="Read-aloud uses" value={a.read_aloud} color="#F7933E" testid="ra-readaloud" />
      </div>
      {a.page_views.length > 0 && (
        <div className="neu-flat rounded-2xl p-4">
          <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Page views</p>
          <div className="flex h-28 items-end gap-1.5" data-testid="ra-page-chart">
            {a.page_views.map((p) => (
              <div key={p.page} className="flex flex-1 flex-col items-center gap-1" title={`Page ${p.page}: ${p.views} views, ${p.readers} readers`}>
                <div className="w-full rounded-t-md sg-gradient" style={{ height: `${(p.views / max) * 90}px` }} />
                <span className="font-mono text-[9px] text-[#64748B]">{p.page}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {a.taps.length > 0 && (
        <div className="neu-flat rounded-2xl p-4" data-testid="ra-taps-list">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Most tapped</p>
          {a.taps.slice(0, 8).map((t, i) => (
            <div key={i} className="flex justify-between border-b border-[#DCE3EE] py-1.5 text-xs last:border-0"><span>{t.name || t.id} <span className="text-[#64748B]">· page {t.page} · {t.type.replace("_", " ")}</span></span><b className="font-mono">{t.count}</b></div>
          ))}
        </div>
      )}
    </div>
  );
};

export const QuizResultsDialog = ({ open, onOpenChange, bookId }) => {
  const [data, setData] = useState(null);
  const [ra, setRa] = useState(null);
  useEffect(() => {
    if (open) {
      setData(null); setRa(null);
      api.get(`/books/${bookId}/quiz-results`).then((r) => setData(r.data));
      api.get(`/books/${bookId}/analytics`).then((r) => setRa(r.data));
    }
  }, [open, bookId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-0 neu-panel thin-scroll" data-testid="quiz-results-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Book analytics</DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">How readers use this book: views, pages read, taps and quiz results.</DialogDescription>
        </DialogHeader>
        {ra ? <ReaderStats a={ra} /> : <div className="grid h-24 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[#3D3DF0]" /></div>}
        <p className="mt-2 font-display text-lg font-bold">Quiz results</p>
        {!data ? <div className="grid h-40 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[#3D3DF0]" /></div> : data.total_attempts === 0 ? (
          <div className="py-10 text-center" data-testid="quiz-results-empty">
            <ListChecks className="mx-auto h-8 w-8 text-[#DCE3EE]" />
            <p className="mt-3 font-display text-lg font-bold">No attempts yet</p>
            <p className="text-sm text-[#64748B]">Add a quiz layer, publish, and results appear here as readers take it.</p>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Attempts" value={data.total_attempts} color="#0F172A" testid="quiz-stat-attempts" />
              <Stat label="Avg score" value={`${data.avg_score}%`} color="#3D3DF0" testid="quiz-stat-avg" />
              <Stat label="Pass rate" value={`${data.pass_rate}%`} color="#5FA82E" testid="quiz-stat-pass" />
            </div>
            {data.quizzes.map((q) => <QuizCard key={q.layer_id} q={q} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
